import { useEffect, useMemo, useState } from 'react';

import type { AnalyticsEvents } from '@sunflower-games/analytics-events';
import classNames from 'classnames';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';

import Button from '../../../../components/buttons/Button';
import { LANGUAGES_OPTIONS } from '../../../../components/ChangeLanguage/constants';
import { getCurrentLanguageOption } from '../../../../components/ChangeLanguage/helpers';
import { CountrySelect } from '../../../../components/CountrySelect/CountrySelect';
import { DialogLoader } from '../../../../components/Dialog/DialogLoader';
import { ErrorMessage } from '../../../../components/ErrorMessage/ErrorMessage';
import { I18Text } from '../../../../components/i18Next/I18Text';
import { LanguageSelect } from '../../../../components/LanguageSelect/LanguageSelect';
import type { LanguageOption } from '../../../../components/LanguageSelect/types';
import { getPhoneErrorMessageMap } from '../../../../components/PhoneNumberInput/constants';
import { TooltipInfoIcon } from '../../../../components/SVGIcons/TooltipInfoIcon';
import { UiRedesignPopperTooltip } from '../../../../components/Tooltip/UiRedesignPopperTooltip';
import { useAvailableLanguageOptions } from '../../../../hooks/useAvailableLanguages';
import { useLocale } from '../../../../hooks/useLocale';
import { useMarketingConsents } from '../../../../hooks/useMarketingConsents';
import i18n from '../../../../i18n/config';
import { analytics } from '../../../../services/analytics';
import { useFlag } from '../../../../services/launchDarkly';
import { useStores } from '../../../../stores/useStores';
import { getErrorMessage } from '../../../../utils/errors';
import { useCountriesData } from '../../../geography/api/countries';
import type { Country } from '../../../geography/types';
import type { ChooseCountryProps } from '../../ChooseCountryDialog/types';
import { SignUpConsentsRedesign } from '../SignUpConsentsRedesign/SignUpConsentsRedesign';

import styles from './ChooseCountryRedesign.module.scss';

export const ChooseCountryComponentRedesign = observer(function ChooseCountryComponentRedesign({
  componentType = 'portal',
  ...props
}: ChooseCountryProps) {
  const { authStore, uiStore, userStore, marketingConsentStore, liveGeoStore } = useStores();
  const [fullCountriesData] = useCountriesData();
  const isLanguageChangeAvailable = useFlag('language_change_available', true);

  const { signUpMarketingConsents, fetchingSignUpMarketingConsent } = marketingConsentStore;

  const {
    marketingConsents,
    marketingConsentsLS,
    onChangeMarketingConsent,
    areAllOptionalConsentsChecked,
    areAllMandatoryConsentsChecked,
    removeMarketingConsentsLS,
  } = useMarketingConsents({
    initialMarketingConsents: signUpMarketingConsents,
    fetchingMarketingConsents: fetchingSignUpMarketingConsent,
    localStorageKey: 'signup-marketing-consents',
  });

  const isCurrentWizardStep = 'isCurrent' in props ? props.isCurrent : false;
  const isActive = isCurrentWizardStep || componentType === 'dialog';

  const { t } = useTranslation();
  const { isGDPR } = useLocale();

  const [error, setError] = useState<string>('');

  const isProcessing = computed(() => authStore.pending('chooseCountry')).get();

  const countryCode = userStore.profile?.cleanGeoData?.countryCode ?? liveGeoStore.countryCode;

  const defaultCountry = useMemo(() => {
    if (countryCode) {
      return fullCountriesData.list.find((country) => country.code === countryCode) || null;
    }
    return null;
  }, [countryCode, fullCountriesData.list]);

  const [selectedCountry, setSelectedCountry] = useState<Country | null>(defaultCountry);

  const initialLanguage = useMemo(() => getCurrentLanguageOption(), []);

  const [selectedLanguage, setSelectedLanguage] = useState(initialLanguage);

  const country = selectedCountry ?? defaultCountry;

  const isCountrySelectDisabled = useMemo(() => {
    return !!country && fullCountriesData.list.length <= 1;
  }, [country, fullCountriesData.list.length]);

  const languageOptions = useAvailableLanguageOptions(LANGUAGES_OPTIONS);
  const disableLanguageSelect = useMemo(() => {
    return languageOptions.length === 1;
  }, [languageOptions]);

  useEffect(() => {
    if (isActive && !languageOptions.some((option) => option.id === selectedLanguage.id)) {
      const fallback = languageOptions[0];
      if (fallback) {
        setSelectedLanguage(fallback);
        void i18n.changeLanguage(fallback.id);
      }
    }
  }, [languageOptions, selectedLanguage.id, isActive]);

  const chooseCountry = async () => {
    try {
      if (!country) {
        return { success: false, message: getPhoneErrorMessageMap().missingCountryError };
      }

      const result = await authStore.chooseCountry({ countryCode: country.code, language: selectedLanguage.id });

      if (marketingConsentsLS) {
        await marketingConsentStore.postMarketingConsents(marketingConsentsLS);
      }

      if (!result.errors?.length) {
        return { success: true, message: '' };
      }
      return { success: false, message: String(result.errors[0]) };
    } catch (err) {
      const msg = getErrorMessage(err);
      return { success: false, message: msg };
    }
  };

  const onChangeLanguage = (newValue: LanguageOption | null) => {
    if (!newValue) {
      return;
    }
    setSelectedLanguage(newValue);
    void i18n.changeLanguage(newValue.id);
  };

  const onSubmit = async () => {
    const { success, message } = await chooseCountry();

    if (!success) {
      const eventParams: AnalyticsEvents['locale_settings'] = {
        event_category: 'sign_up',
        event_action: 'click',
        country: country?.code || '',
        button_text: 'done',
        locale: selectedLanguage.id,
        status: 'error',
        error_message: message,
        commercial_ind: areAllOptionalConsentsChecked && areAllMandatoryConsentsChecked ? 1 : 0,
      };
      void analytics.sendEvent('locale_settings', eventParams);
      setError(message);
      return;
    }

    const localeSettingsSuccessEventParams: AnalyticsEvents['locale_settings'] = {
      event_category: 'sign_up',
      event_action: 'click',
      country: country?.code || '',
      button_text: 'done',
      locale: selectedLanguage.id,
      status: 'success',
      commercial_ind: areAllOptionalConsentsChecked && areAllMandatoryConsentsChecked ? 1 : 0,
    };

    void analytics.sendEvent('locale_settings', localeSettingsSuccessEventParams);

    if (componentType === 'portal' && props.nextStep) {
      props.nextStep();
    } else {
      uiStore.setOpenDialog({ dialog: null });
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    if (!country || !areAllMandatoryConsentsChecked) {
      setError(t('auth.pleaseFillInAllRequiredFields'));
      return;
    }

    void onSubmit();
  };

  const sweepsPolicyUrl = isGDPR ? t('sweeps.policy.superUrl') : t('sweeps.policy.url');

  useEffectOnce(() => {
    removeMarketingConsentsLS();
  });

  useEffect(() => {
    if (isActive) {
      const localeSettingsImpressionEventParams: AnalyticsEvents['locale_settings'] = {
        event_category: 'sign_up',
        event_action: 'impression',
      };
      void analytics.sendEvent('locale_settings', localeSettingsImpressionEventParams);
    }
  }, [isActive]);

  useEffect(() => {
    if (fullCountriesData.list?.length === 1) {
      setSelectedCountry(fullCountriesData.list[0]);
    }
  }, [fullCountriesData.list]);

  return (
    <>
      <DialogLoader isProcessing={isProcessing} />

      <div className={styles.containerContent}>
        <form onSubmit={handleSubmit}>
          <div className={classNames('form-field', styles.selectsContainer)}>
            <label className={classNames(styles.label)} htmlFor="country">
              {t('forms.country').toLowerCase()}
              <UiRedesignPopperTooltip
                isBig
                content={
                  <div className={classNames(styles.labelTooltipContent, styles.labelTooltipContentCountry)}>
                    {t('choseSettings.countryHint')}
                  </div>
                }
                offset={[-35, 0]}
                placement="bottom-start"
                wrapperClassName={styles.labelTooltipWrapper}
              >
                <TooltipInfoIcon />
              </UiRedesignPopperTooltip>
            </label>
            <CountrySelect
              classNames={{
                wrapper: styles.countryWrapper,
                control: error && !selectedCountry ? styles.errorStyleInput : '',
              }}
              disabled={isCountrySelectDisabled}
              name="country"
              options={fullCountriesData.list}
              value={country}
              onChange={setSelectedCountry}
            />
            {isLanguageChangeAvailable && (
              <>
                <label className={classNames(styles.label)} htmlFor="language">
                  {t('user.profile.languageUpperCase').toLowerCase()}
                  <UiRedesignPopperTooltip
                    isBig
                    content={<div className={styles.labelTooltipContent}>{t('choseSettings.languageHint')}</div>}
                    offset={[-35, 0]}
                    placement="bottom-start"
                    wrapperClassName={styles.labelTooltipWrapper}
                  >
                    <TooltipInfoIcon />
                  </UiRedesignPopperTooltip>
                </label>
                <LanguageSelect
                  classNames={{
                    wrapper: styles.languageWrapper,
                  }}
                  customStyles={{
                    languageName: {
                      marginLeft: '1.1rem',
                    },
                  }}
                  disabled={disableLanguageSelect}
                  hideDropdownArrow={disableLanguageSelect}
                  name="language"
                  options={languageOptions}
                  value={selectedLanguage}
                  onChange={onChangeLanguage}
                />
              </>
            )}
          </div>

          <SignUpConsentsRedesign
            marketingConsents={marketingConsents}
            showRequiredError={!!error}
            signUpMarketingConsents={signUpMarketingConsents}
            onChangeMarketingConsent={onChangeMarketingConsent}
          />

          <ErrorMessage className={styles.errorMessage}>{error}</ErrorMessage>

          <Button
            btnType="main"
            classNameWrapper={styles.submitButton}
            dataTestId="choose-country-done-btn"
            type="submit"
          >
            {t('poc.doneUpperCase')}
          </Button>

          <I18Text
            className={styles.privacyAcceptText}
            components={{
              linkText: <a href={`/pages/${sweepsPolicyUrl}`} rel="noreferrer" target="_blank" />,
            }}
            i18nKey="choseSettings.termsConsent"
            values={{ sweepsOrSuper: !isGDPR, context: countryCode }}
          />
        </form>
      </div>
    </>
  );
});
